'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const USAGE_FRESHNESS_THRESHOLD_MS = 30000;

/**
 * Track freshness of a prefetched usage payload and trigger a callback when it becomes stale.
 *
 * @param {number|null} prefetchedAt Timestamp (Date.now) captured when the SSR fetch ran.
 * @param {(() => void)|null} onStale Callback invoked once when the snapshot becomes stale.
 * @returns {{ isFresh: boolean, age: number, refetch: () => void }} Derived freshness info and manual refetch helper.
 */
export function useUsageFreshness(prefetchedAt = null, onStale = null) {
  const initialFresh = useMemo(() => {
    if (typeof prefetchedAt !== 'number' || Number.isNaN(prefetchedAt)) {
      return false;
    }

    return Date.now() - prefetchedAt < USAGE_FRESHNESS_THRESHOLD_MS;
  }, [prefetchedAt]);

  const [age, setAge] = useState(() => {
    if (typeof prefetchedAt === 'number' && !Number.isNaN(prefetchedAt)) {
      return Date.now() - prefetchedAt;
    }

    return Infinity;
  });
  const [isFresh, setIsFresh] = useState(initialFresh);
  const wasFreshRef = useRef(initialFresh);

  const triggerStale = useCallback(() => {
    if (typeof onStale === 'function') {
      onStale();
    }
  }, [onStale]);

  const checkFreshness = useCallback(() => {
    if (typeof prefetchedAt !== 'number' || Number.isNaN(prefetchedAt)) {
      setAge(Infinity);
      setIsFresh(false);
      wasFreshRef.current = false;
      return;
    }

    const now = Date.now();
    const currentAge = now - prefetchedAt;
    setAge(currentAge);

    const fresh = currentAge < USAGE_FRESHNESS_THRESHOLD_MS;
    if (wasFreshRef.current && !fresh) {
      triggerStale();
    }
    wasFreshRef.current = fresh;
    setIsFresh(fresh);
  }, [prefetchedAt, triggerStale]);

  useEffect(() => {
    wasFreshRef.current = initialFresh;
    setIsFresh(initialFresh);
    if (typeof prefetchedAt === 'number' && !Number.isNaN(prefetchedAt)) {
      setAge(Date.now() - prefetchedAt);
    } else {
      setAge(Infinity);
    }
  }, [initialFresh, prefetchedAt]);

  useEffect(() => {
    checkFreshness();
    const interval = setInterval(checkFreshness, 1000);
    return () => clearInterval(interval);
  }, [checkFreshness]);

  const refetch = useCallback(() => {
    triggerStale();
  }, [triggerStale]);

  return { isFresh, age, refetch };
}

export default useUsageFreshness;

